import React, { useEffect, useState, useCallback } from 'react';
import { X, Link2, Copy, Check, Trash2, ExternalLink, Loader2 } from 'lucide-react';
import { toast } from '../utils/toast';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ShareLinkModal — owner-side modal for managing the public storyboard link.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   projectId: string
 *   authToken: string
 *   projectName: string
 */
export default function ShareLinkModal({ open, onClose, projectId, authToken, projectName }) {
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(null);
  const [createdAt, setCreatedAt] = useState(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  const buildUrl = (t) =>
    `${window.location.origin}/share/storyboard/${t}`;

  const fetchLink = useCallback(async () => {
    if (!projectId || !authToken) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/projects/${projectId}/share-link`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.ok) {
        const j = await res.json();
        setToken(j.token || null);
        setCreatedAt(j.created_at || null);
      } else {
        setToken(null);
      }
    } catch {
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, [projectId, authToken]);

  useEffect(() => {
    if (open) fetchLink();
  }, [open, fetchLink]);

  const enable = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/projects/${projectId}/share-link`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) throw new Error('Failed to create link');
      const j = await res.json();
      setToken(j.token);
      setCreatedAt(j.created_at);
      toast.success('Public link enabled');
    } catch (e) {
      toast.error(`Enable failed: ${e.message || e}`);
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    if (!window.confirm('Revoke this public link? Anyone using it will get a 404.')) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/projects/${projectId}/share-link`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) throw new Error('Failed to revoke');
      setToken(null);
      setCreatedAt(null);
      toast.success('Public link revoked');
    } catch (e) {
      toast.error(`Revoke failed: ${e.message || e}`);
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async () => {
    if (!window.confirm('Generate a new link? The current URL will stop working.')) return;
    setBusy(true);
    try {
      // Revoke then create
      await fetch(`${API}/projects/${projectId}/share-link`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const res = await fetch(`${API}/projects/${projectId}/share-link`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) throw new Error('Failed to regenerate');
      const j = await res.json();
      setToken(j.token);
      setCreatedAt(j.created_at);
      toast.success('New link generated');
    } catch (e) {
      toast.error(`Regenerate failed: ${e.message || e}`);
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (!token) return;
    try {
      await navigator.clipboard.writeText(buildUrl(token));
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error('Could not copy — long-press to select');
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="share-link-modal-backdrop"
    >
      <div
        className="w-full max-w-lg rounded-2xl shadow-2xl border border-white/10"
        style={{ background: 'rgba(19, 27, 44, 0.98)' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="share-link-modal"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="flex items-center gap-2.5">
            <Link2 className="w-4 h-4 text-cyan-400" />
            <div>
              <div className="text-sm font-semibold text-white">Share Storyboard</div>
              <div className="text-[11px] text-white/50 truncate max-w-[300px]">{projectName}</div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
            data-testid="share-modal-close-btn"
          >
            <X className="w-4 h-4 text-white/60" />
          </button>
        </div>

        <div className="px-5 py-5">
          {loading ? (
            <div className="flex items-center gap-2 text-white/60 text-sm py-4">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading link…
            </div>
          ) : token ? (
            <>
              <div className="text-[11px] uppercase tracking-wider text-white/40 mb-2">Public link</div>
              <div className="flex items-stretch gap-2">
                <input
                  type="text"
                  readOnly
                  value={buildUrl(token)}
                  onFocus={(e) => e.target.select()}
                  className="flex-1 px-3 py-2 rounded-lg text-xs bg-black/40 border border-white/10 text-white/90 font-mono"
                  data-testid="share-link-url-input"
                />
                <button
                  onClick={copyLink}
                  className="px-3 py-2 rounded-lg text-xs font-medium bg-cyan-500/15 text-cyan-300 hover:bg-cyan-500/25 border border-cyan-500/30 flex items-center gap-1.5 whitespace-nowrap transition-all"
                  data-testid="share-link-copy-btn"
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              {createdAt && (
                <div className="text-[10px] text-white/40 mt-2">
                  Created {new Date(createdAt).toLocaleString()}
                </div>
              )}

              <div className="flex flex-wrap gap-2 mt-5">
                <a
                  href={buildUrl(token)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap bg-white/5 text-white/80 hover:bg-white/10 border border-white/10 flex items-center gap-1.5 transition-all"
                  data-testid="share-link-preview-btn"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Preview
                </a>
                <button
                  onClick={regenerate}
                  disabled={busy}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 border border-amber-500/30 flex items-center gap-1.5 transition-all disabled:opacity-50"
                  data-testid="share-link-regenerate-btn"
                >
                  Regenerate
                </button>
                <button
                  onClick={disable}
                  disabled={busy}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/30 flex items-center gap-1.5 transition-all disabled:opacity-50 ml-auto"
                  data-testid="share-link-revoke-btn"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Revoke
                </button>
              </div>

              <div className="mt-5 px-3 py-2.5 rounded-lg bg-cyan-500/5 border border-cyan-500/15 text-[11px] text-cyan-200/80 leading-relaxed">
                Anyone with this link can view your project's logline, locked keyframes, asset bible, and download the storyboard PDF. No login required. Revoke anytime.
              </div>
            </>
          ) : (
            <>
              <div className="text-sm text-white/80 leading-relaxed mb-4">
                Generate a public, read-only link to share this storyboard with clients or your team.
                No login required for viewers.
              </div>
              <ul className="text-[12px] text-white/60 space-y-1.5 mb-5">
                <li>• Project name, logline & duration</li>
                <li>• Locked keyframe gallery</li>
                <li>• Asset Bible (cast, costumes, locations)</li>
                <li>• Storyboard PDF download</li>
              </ul>
              <button
                onClick={enable}
                disabled={busy}
                className="w-full px-4 py-2.5 rounded-lg text-sm font-semibold bg-gradient-to-r from-cyan-500 to-cyan-600 text-white hover:from-cyan-600 hover:to-cyan-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="share-link-enable-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                Enable public link
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
